import { STYLE_ALIASES, ANGLE_ALIASES, type AdStyle, type AdAngle, type AdFilter, normalizeAdFilter } from './constants/adStyles';
import type { CreativeAllocation } from './creativeAllocation';

export type AdKeyword = { axis: 'styles'; value: AdStyle; start: number; end: number }
  | { axis: 'angles'; value: AdAngle; start: number; end: number };
const tagSlug = (value: string) => value.toLowerCase().replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
export function inspirationTag(label: string, axis?: 'style' | 'angle'): string {
  const slug = tagSlug(label);
  return '@' + (axis === 'angle' && slug === 'testimonial' ? 'testimonial-angle' : slug);
}
const tags = new Map<string, Pick<AdKeyword, 'axis' | 'value'>>();
for (const [alias, value] of Object.entries(ANGLE_ALIASES)) {
  tags.set(inspirationTag(alias), {axis:'angles', value});
  tags.set(inspirationTag(value, 'angle'), {axis:'angles', value});
}
for (const [alias, value] of Object.entries(STYLE_ALIASES)) {
  tags.set(inspirationTag(alias), {axis:'styles', value});
  tags.set(inspirationTag(value, 'style'), {axis:'styles', value});
}

/** A rejected direction is context, never an automatically selected filter. */
export function isNegatedAdKeyword(text: string, start: number, end: number): boolean {
  const before = text.slice(0, start).toLowerCase().replace(/[’‘]/g, "'").split(/[.!?;,\n]|\bbut\b|\binstead use\b/).at(-1) ?? '';
  const after = text.slice(end).toLowerCase();
  return /\b(?:instead of|rather than|without|avoid|skip|exclude|excluding|no|not|never|don't|do not)\s+(?:(?:the|an?|any|that|this|use|using|want|include|add|adding|choose|pick|previous|earlier|suggested)\s+){0,5}$/.test(before)
    || /^\s*(?:(?:style|angle|direction)\s+)?(?:is\s+)?(?:not wanted|isn't wanted|shouldn't be used|should not be used|is excluded)\b/.test(after);
}

/** First explicit inspiration tag wins; ordinary words never select a template. */
export function firstAdKeyword(text: string): AdKeyword | null {
  // Attachment transport and URLs must never become invisible selections.
  const visible = text.replace(/\[(?:Attached|Reference) image(?: to clone)?:[^\]]+\]|https?:\/\/[^\s]+/gi, value => ' '.repeat(value.length));
  const pattern = new RegExp('(?<![\\p{L}\\p{N}_.+@-])@[a-z0-9]+(?:-[a-z0-9]+)*(?![\\p{L}\\p{N}_@-])', 'giu');
  for (const match of Array.from(visible.matchAll(pattern))) {
    const tag = tags.get(match[0].toLowerCase());
    const start = match.index!;
    const end = start + match[0].length;
    if (tag && !isNegatedAdKeyword(visible, start, end)) return {...tag, start, end} as AdKeyword;
  }
  return null;
}
export function createAdFilter(text: string): AdFilter {
  const keyword = firstAdKeyword(text);
  return {styles:keyword?.axis === 'styles' ? [keyword.value] : null,
    angles:keyword?.axis === 'angles' ? [keyword.value] : null};
}
/** Keep the first batch quantity rather than summing later keyword quotas. */
export function createAdCount(text: string, fallback = 20): number {
  if(/\b(?:make|create|generate|render|build|give\s+me|want|need)\s+(?:me\s+)?(?:a|an|single)\s+(?:[\w-]+\s+){0,3}(?:ad|image|creative)(?=\s|[.!?,;]|$)/i.test(text))return 1;
  const words:Record<string,number>={one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,ten:10,eleven:11,twelve:12,twenty:20,thirty:30,forty:40,fifty:50,hundred:100};
  const quantity=`(?:\\d{1,6}|${Object.keys(words).join('|')})`;
  const match = new RegExp(`\\b(?:make|create|generate|build|give\\s+me|want|need)\\s+(?:me\\s+)?(?:exactly\\s+)?(${quantity})\\b`,'i').exec(text)
    ?? new RegExp(`\\b(${quantity})\\s+(?:[\\p{L}@& -]+\\s+)?(?:ads?|images?|creatives?)\\b`,'iu').exec(text);
  const count = match ? Number(match[1]) || words[match[1].toLowerCase()] : fallback;
  return count > 0 ? count : fallback;
}
export function templateAdPrompt(style: string, existing: string, axis?: 'style' | 'angle'): string {
  const tag = inspirationTag(style, axis);
  const keyword = tags.has(tag) ? tag : style;
  return `Make me ${createAdCount(existing)} ${keyword} ads.`;
}
/** One batch only; subsequent plain-text keywords do not introduce quotas. */
export function createAdAllocations(text: string, count: number, filter: {styles?:string[] | null;angles?:string[] | null} = createAdFilter(text)): CreativeAllocation[] {
  const selected=normalizeAdFilter(filter.styles,filter.angles);
  return [{count,...(selected.styles?.[0] ? {style:selected.styles[0]} : selected.angles?.[0] ? {angle:selected.angles[0]} : {})}];
}

/** Mark the batch format explicitly so later plain keywords cannot change anatomy. */
export function createAdPlanningBrief(text:string, allocation:CreativeAllocation):string {
  return text+'\nOnly the first recognized @inspiration tag is the explicit batch selection. Keep that selection for every ad. Ordinary words do not select an inspiration template. Treat exclusions as exclusions. Choose a varied mix on the other axis automatically from compatible winning ads.'
    +'\nREQUIRED BATCH FORMAT: '+(allocation.style ?? 'unspecified');
}
